import { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router';
import { Alert, Button, Card, IconCheckCircle, IconWarningCircle, TextLink } from '@indipia/ui-core';
import {
  ShareLoadSkeleton,
  ShareStateScreen,
  classifyShareError,
  dateOnly,
  formatMonthDayDow,
  useShareResource,
} from '@indipia/shared-features';
import { fetchOrderShare, issueOrderListLink, postOrderReply } from '../../api/orderReplyShareApi';
import { OrderContentCard } from '../../components/share/OrderContentCard';
import { OrderReplyForm } from '../../components/share/OrderReplyForm';
import { LIST_PATH, parseOrderVendorId } from './vendorOrderList.logic';
import {
  TEXT,
  asReplyFieldKey,
  declineReasonLabel,
  formatReplyDtm,
  inspectOrderShare,
  replySummaryLine,
  type OrderShareData,
  type OrderView,
  type ReplyFieldKey,
  type ReplyPayload,
} from './vendorOrderReply.logic';

/**
 * C-3-1 거래처 발주 회신 (외부·무인증 공유 화면, share/SUPPLIER_ORDER_REPLY).
 * 거래처가 알림톡 [발주내용 확인·회신하기] 버튼으로 연다. 자격증명은 공유 쿠키(share_jwt)뿐이며
 * 화면은 토큰·shareId 를 다루지 않는다. 받은 발주 목록(share/SUPPLIER_ORDER_LIST)은 헤더 [☰ 받은 발주]·회신 완료 [받은 발주 전체 보기]로 이동한다.
 */

type ScreenKind = 'SESSION' | 'LINK' | 'CLOSED' | 'EXPIRED' | 'CHANGED' | 'NETWORK' | 'SERVER' | 'UNKNOWN';
const SCREEN_KINDS = new Set<string>(['SESSION', 'LINK', 'CLOSED', 'EXPIRED', 'CHANGED', 'NETWORK', 'SERVER', 'UNKNOWN']);
const RETRYABLE = new Set<ScreenKind>(['NETWORK', 'SERVER', 'UNKNOWN']);

function Shell({ children }: { children: React.ReactNode }) {
  // data-group="03" = 인디플래너 그룹 액센트
  return (
    <div data-group="03" className="min-h-screen" style={{ background: 'var(--shell-bg)' }}>
      <div className="mx-auto flex min-h-screen w-full max-w-[480px] flex-col gap-4 p-4" style={{ background: 'var(--shell-bg)' }}>
        {children}
      </div>
    </div>
  );
}

export function VendorOrderReplyPage() {
  const navigate = useNavigate();
  // 받은 발주 목록에서 들어온 화면이면 헤더·완료 버튼은 목록 링크를 새로 발급하지 않고 목록 화면으로 돌아간다.
  const fromList = useMemo(() => parseOrderVendorId(window.location.search) !== null, []);
  const res = useShareResource<OrderShareData>((signal) => fetchOrderShare(signal));
  const { setData } = res;
  const [override, setOverride] = useState<ScreenKind | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const submittingRef = useRef(false);
  const [formError, setFormError] = useState<string | null>(null);
  const [fieldErrors, setFieldErrors] = useState<Partial<Record<ReplyFieldKey, string>>>({});
  const [notice, setNotice] = useState<'done' | 'already' | null>(null);
  const headingRef = useRef<HTMLHeadingElement>(null);
  const [listOpening, setListOpening] = useState(false);
  const [listError, setListError] = useState(false);

  const inspected = useMemo(() => (res.status === 'ready' ? inspectOrderShare(res.data) : null), [res.status, res.data]);

  useEffect(() => {
    document.title = TEXT.documentTitle;
  }, []);

  const screenKey = override ?? res.status + (inspected?.kind ?? '');
  useEffect(() => {
    headingRef.current?.focus();
  }, [screenKey]);

  // ── 화면 전환 ──
  if (override) {
    const name = inspected && (inspected.kind === 'OPEN' || inspected.kind === 'REPLIED') ? inspected.view.companyName : undefined;
    return <ShareStateScreen kind={override} companyName={name} />; // 제출 중 생긴 오류 화면은 GET 재조회로 풀리지 않으므로 재시도 버튼 없음(§4.3)
  }
  if (res.status === 'loading') return <ShareLoadSkeleton />;
  if (res.status === 'error') {
    const kind: ScreenKind = SCREEN_KINDS.has(res.error?.kind ?? '') ? (res.error!.kind as ScreenKind) : 'UNKNOWN';
    return <ShareStateScreen kind={kind} onRetry={RETRYABLE.has(kind) ? res.reload : undefined} />;
  }
  if (!inspected || inspected.kind === 'UNKNOWN') return <ShareStateScreen kind="UNKNOWN" onRetry={res.reload} />;
  if (inspected.kind !== 'OPEN' && inspected.kind !== 'REPLIED') return <ShareStateScreen kind={inspected.kind} />;

  const view: OrderView = inspected.view;

  // B1 재조회로 REPLIED 확인 (응답 없음·이미 회신 처리 공통). 성공하면 true.
  const recheckReplied = async (): Promise<boolean> => {
    try {
      const data = await fetchOrderShare();
      const r = inspectOrderShare(data);
      if (r.kind === 'REPLIED') {
        setData(data);
        return true;
      }
      if (r.kind === 'CLOSED' || r.kind === 'EXPIRED' || r.kind === 'CHANGED') {
        setData(data);
        return true;
      }
    } catch {
      /* 확인 실패는 호출측에서 오류 문구로 처리 */
    }
    return false;
  };

  const handleSubmitError = async (err: unknown) => {
    const e = classifyShareError(err);
    switch (e.kind) {
      case 'SESSION':
      case 'LINK':
      case 'CLOSED':
      case 'EXPIRED':
      case 'CHANGED':
        setOverride(e.kind);
        return;
      case 'ALREADY_RESPONDED':
        if (await recheckReplied()) setNotice('already');
        else setFormError(TEXT.serverRetry);
        return;
      case 'NETWORK':
        // 응답을 못 받아 결과를 모름 — 재전송하지 않고 B1 로 확인
        if (await recheckReplied()) setNotice('done');
        else setFormError(TEXT.networkNotSent);
        return;
      case 'VALIDATION': {
        const key = asReplyFieldKey(e.field);
        if (key) {
          const msg = key === 'replyNote' ? TEXT.noteTooLong : key === 'availDate' ? TEXT.availDatePast : TEXT.fieldCheck;
          setFieldErrors({ [key]: msg });
        } else {
          setFormError(TEXT.fieldCheck);
        }
        return;
      }
      case 'SERVER':
        setFormError(TEXT.serverRetry);
        return;
      default:
        setOverride('UNKNOWN');
    }
  };

  const handleSubmit = async (payload: ReplyPayload) => {
    if (submittingRef.current) return;
    submittingRef.current = true;
    setSubmitting(true);
    setFormError(null);
    setFieldErrors({});
    try {
      const r = await postOrderReply(payload);
      if (r.state === 'REPLIED' && r.replyDtm) {
        setData({
          ...(res.data as OrderShareData),
          state: 'REPLIED',
          reply: {
            replyResultCode: payload.replyResultCode,
            replyDtm: r.replyDtm,
            replyReasonCode: payload.replyReasonCode ?? null,
            availDate: payload.availDate ?? null,
            liftAvailYn: payload.liftAvailYn ?? null,
            replyNote: payload.replyNote ?? null,
          },
        });
        setNotice('done');
      } else if (!(await recheckReplied())) {
        // 200 인데 응답 형태가 약속과 다름 — 값을 만들어 채우지 않고 서버 상태로 확인
        setOverride('UNKNOWN');
      } else {
        setNotice('done');
      }
    } catch (err) {
      await handleSubmitError(err);
    } finally {
      submittingRef.current = false;
      setSubmitting(false);
    }
  };

  // 받은 발주 목록 링크 발급(E-12) 후 URL 전체 이동 — 쿠키에는 토큰 하나만 담기므로 SPA 이동이 아니라 페이지 이동.
  const openOrderList = async () => {
    if (fromList) {
      navigate(LIST_PATH);
      return;
    }
    if (listOpening) return;
    setListOpening(true);
    setListError(false);
    try {
      window.location.assign(await issueOrderListLink());
    } catch {
      setListError(true);
      setListOpening(false);
    }
  };

  const subtitle =
    view.sendTypeCode === 'ORDER_CHANGE' ? TEXT.subtitleChange : view.companyName ? TEXT.subtitleRequest(view.companyName) : null;

  return (
    <Shell>
      <header>
        <div className="flex items-start justify-between gap-2">
          <h1 ref={headingRef} tabIndex={-1} className="text-lg font-bold text-text-primary outline-none">{TEXT.heading}</h1>
          <TextLink onClick={openOrderList} disabled={listOpening} aria-label={fromList ? undefined : TEXT.orderListLink}>{fromList ? TEXT.backToList : TEXT.headerOrderList}</TextLink>
        </div>
        {subtitle && <p className="mt-1 text-sm text-text-secondary">{subtitle}</p>}
        {listError && <Alert kind="error" role="alert" className="mt-2">{TEXT.orderListFail}</Alert>}
      </header>

      {inspected.kind === 'OPEN' ? (
        <>
          <OrderContentCard view={view} />
          <p className="text-sm text-text-muted">{TEXT.linkNotice(formatMonthDayDow(view.expiresDate))}</p>
          <OrderReplyForm view={view} submitting={submitting} formError={formError} serverFieldErrors={fieldErrors} onSubmit={handleSubmit} />
        </>
      ) : (
        <RepliedSection view={view} notice={notice} listOpening={listOpening} onOpenList={openOrderList} />
      )}
    </Shell>
  );
}

function RepliedSection({
  view,
  notice,
  listOpening,
  onOpenList,
}: {
  view: OrderView;
  notice: 'done' | 'already' | null;
  listOpening: boolean;
  onOpenList: () => void;
}) {
  const reply = view.reply!;
  const accepted = reply.replyResultCode === 'ACCEPTED';
  return (
    <>
      {notice === 'done' && (
        <div aria-live="polite" className="flex flex-col gap-1">
          <div className="flex items-center gap-2 text-base font-bold text-text-primary">
            <IconCheckCircle width={20} height={20} aria-hidden="true" /> {TEXT.doneTitle}
          </div>
          <p className="text-sm text-text-secondary">{replySummaryLine(reply, view.deliveryDate)}</p>
          <p className="text-sm text-text-secondary">{TEXT.doneNotice}</p>
        </div>
      )}
      {notice === 'already' && <Alert kind="info" aria-live="polite">{TEXT.alreadyReplied}</Alert>}

      <OrderContentCard view={view} />

      <Card aria-label={TEXT.repliedCardTitle}>
        <h2 className="text-sm font-bold text-text-primary">{TEXT.repliedCardTitle}</h2>
        <dl className="mt-2 flex flex-col gap-1 text-sm">
          <div className="flex items-center gap-2 text-text-primary">
            {accepted ? <IconCheckCircle width={16} height={16} aria-hidden="true" /> : <IconWarningCircle width={16} height={16} aria-hidden="true" />}
            <span className="font-bold">{accepted ? TEXT.resultAccepted : TEXT.resultDeclined}</span>
          </div>
          {accepted && reply.liftAvailYn && (
            <div className="text-text-primary">{reply.liftAvailYn === 'Y' ? TEXT.liftAvailable : TEXT.liftUnavailable}</div>
          )}
          {!accepted && (
            <>
              <div className="text-text-primary">{declineReasonLabel(reply.replyReasonCode)}</div>
              {reply.availDate && (
                <div className="text-text-primary">{TEXT.availDateLabel} {formatMonthDayDow(dateOnly(reply.availDate))}</div>
              )}
              {reply.replyNote && <div className="break-words text-text-primary">{reply.replyNote}</div>}
            </>
          )}
          <div className="text-text-muted">{formatReplyDtm(reply.replyDtm)}</div>
        </dl>
      </Card>

      <p className="text-sm text-text-secondary">{TEXT.repliedNotice}</p>

      <Button variant="secondary" size="lg" className="w-full" loading={listOpening} onClick={onOpenList}>
        {TEXT.orderListLink}
      </Button>
    </>
  );
}
